import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Brush, Upload } from "lucide-react";
import { toast } from "sonner";

import PageTitleBar from "@/components/PageTitleBar";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import { useEmailAttachment } from "hooks/useEmailAttachment";
import usePageTitle from "hooks/usePageTitle";
import EmailAttachmentForm from "routes/email/forms/EmailAttachmentForm";
import EmailAttachmentGraphicalForm from "routes/email/forms/EmailAttachmentGraphicalForm";

const LIST_PATH = "/settings/email_attachments";

const MODE_FILE = "file";
const MODE_GRAPHICAL = "graphical";

const EmailAttachment = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailAttachments.edit
      : ACL_RELATIONSHIPS.emailAttachments.create,
  ]);

  const {
    data: attachmentData,
    isSuccess: attachmentLoaded,
    isError: attachmentError,
    isFetchedAfterMount,
  } = useEmailAttachment(sourceId);

  const pageTitle = isEdit
    ? "Edit Email Attachment"
    : isClone
      ? "Clone Email Attachment"
      : "New Email Attachment";

  usePageTitle(isEdit ? attachmentData?.name : pageTitle);

  const handleSuccess = () => {
    toast.success(
      isEdit
        ? "Email attachment updated"
        : isClone
          ? "Email attachment cloned"
          : "Email attachment created"
    );
    navigate(LIST_PATH);
  };

  const handleCancel = () => navigate(LIST_PATH);

  if (!canWrite) {
    return <Navigate to={LIST_PATH} replace />;
  }

  if (attachmentError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this email attachment. Please try again."}</p>
        <Link to={LIST_PATH}>Back to Email Attachments</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // the form's first render. Mounting on that payload would strand the form on
  // pre-save values, and saving would write them back over a newer edit.
  if (sourceId && !(attachmentLoaded && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  const formProps = {
    attachmentId: isEdit ? id : null,
    isClone,
    initialData: attachmentData ?? {},
    onSuccess: handleSuccess,
    onCancel: handleCancel,
  };

  let content;
  if (sourceId) {
    content = attachmentData.is_graphical ? (
      <EmailAttachmentGraphicalForm {...formProps} />
    ) : (
      <EmailAttachmentForm {...formProps} />
    );
  } else {
    // Hidden panels unmount, so switching modes discards the other form's
    // unsaved input.
    content = (
      <Tabs defaultValue={MODE_FILE} className="tw:gap-4">
        <TabsList>
          <TabsTrigger value={MODE_FILE}>
            <Upload />
            Upload File
          </TabsTrigger>
          <TabsTrigger value={MODE_GRAPHICAL}>
            <Brush />
            Graphical Editor
          </TabsTrigger>
        </TabsList>
        <TabsContent value={MODE_FILE}>
          <EmailAttachmentForm {...formProps} />
        </TabsContent>
        <TabsContent value={MODE_GRAPHICAL}>
          <EmailAttachmentGraphicalForm {...formProps} />
        </TabsContent>
      </Tabs>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col">
        <PageTitleBar
          backTo={LIST_PATH}
          backLabel="Back to Email Attachments"
          title={pageTitle}
          className="tw:mb-5"
        />
        {content}
      </div>
    </div>
  );
};

export default EmailAttachment;
